import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AVAILABILITY, PROVIDER_SERVICES } from "./data";
import { Reveal, EASE } from "./motion";
import { Field, Chips } from "./FormBits";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { full_name: "", company: "", email: "", phone: "", location: "", experience: "", insured: "yes", message: "", services: [], availability: [] };

export const ProviderForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.services.length) return toast.error("Pick at least one service you offer.");
    if (!API) return toast.error("Applications are temporarily unavailable. Please call 717.691.1035.");
    setStatus("loading");
    try {
      await axios.post(`${API}/provider-applications`, form);
      setStatus("done");
      setForm(EMPTY);
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? "Please check your email and phone number." : "Something went wrong. Please try again or call us.");
    }
  };

  return (
    <Reveal className="relative rounded-[28px] border border-white/10 bg-[#171717]/80 p-6 backdrop-blur-xl sm:p-10" data-testid="provider-application">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-brand-400">Provider application</div>
          <h3 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white">Apply in two minutes</h3>
        </div>
        <span className="hidden font-mono text-xs text-zinc-400 sm:block">All fields marked * are required</span>
      </div>
      <AnimatePresence initial={false}>
        {status === "done" ? (
          <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="py-16 text-center" data-testid="provider-application-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-brand-400" strokeWidth={1.5} />
            <h4 className="mt-6 font-display text-3xl font-bold text-white">Application received.</h4>
            <p className="mx-auto mt-3 max-w-sm text-zinc-300">Thanks for reaching out. Our team will review your details and contact you about upcoming opportunities.</p>
            <button onClick={() => setStatus("idle")} data-testid="provider-application-new" className="mt-8 text-sm font-semibold text-brand-300 underline underline-offset-4 hover:text-brand-200">Submit another application</button>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="provider-application-form">
            <Field label="Full name *" id="full_name" value={form.full_name} onChange={set("full_name")} required minLength={2} />
            <Field label="Company (if any)" id="company" value={form.company} onChange={set("company")} />
            <Field label="Email *" id="email" type="email" value={form.email} onChange={set("email")} required />
            <Field label="Phone *" id="phone" type="tel" value={form.phone} onChange={set("phone")} required minLength={7} />
            <Field label="City & state *" id="location" value={form.location} onChange={set("location")} required minLength={2} placeholder="e.g. York, PA" />
            <Field label="Years of experience" id="experience" as="select" value={form.experience} onChange={set("experience")} options={["", "Less than 1", "1–3", "3–5", "5–10", "10+"]} />
            <Chips label="Services you offer *" name="services" options={PROVIDER_SERVICES} selected={form.services} onToggle={(v) => toggle("services", v)} />
            <Chips label="Availability" name="availability" options={AVAILABILITY} selected={form.availability} onToggle={(v) => toggle("availability", v)} />
            <Chips label="Do you carry liability insurance? *" name="insured" single options={[["yes", "Yes"], ["in-progress", "In progress"], ["no", "Not yet"]]} selected={[form.insured]} onToggle={(v) => setForm((f) => ({ ...f, insured: v }))} />
            <Field label="Tell us about your crew & equipment" id="message" as="textarea" value={form.message} onChange={set("message")} className="sm:col-span-2" />
            <button type="submit" disabled={status === "loading"} data-testid="provider-application-submit" className="group mt-2 inline-flex items-center justify-center gap-3 rounded-full bg-brand-400 px-8 py-4 font-display text-base font-bold text-[#0A0A0A] transition-[background-color,transform] duration-300 hover:scale-[1.02] hover:bg-brand-300 disabled:opacity-70 sm:col-span-2 sm:justify-self-start">
              {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              Submit application
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </Reveal>
  );
};
